import { useState } from 'react';
import { toast } from '@/lib/toast';
import type { VariantFieldUpdate } from '@/lib/api';
import { useBulkSetNurseryPrices, useBulkUpdateVariantFields } from '@/hooks/useProducts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterChips } from '@/components/ui/filter-chips';
import type { BulkTargets } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targets: BulkTargets;
  onApplied: () => void;
}

type PriceTarget = 'nursery' | 'online' | 'compare_at';
type Mode = 'fixed' | 'match' | 'pct' | 'delta' | 'clear';

const TARGET_CHIPS = [
  { value: 'nursery', label: 'Nursery price' },
  { value: 'online', label: 'Online price' },
  { value: 'compare_at', label: 'Compare-at price' },
];

/** Online price can't be cleared or "matched to itself". */
const MODE_CHIPS: Record<PriceTarget, { value: string; label: string }[]> = {
  nursery: [
    { value: 'fixed', label: 'Fixed price' },
    { value: 'match', label: 'Match online' },
    { value: 'pct', label: '% of online' },
    { value: 'delta', label: '+/− online' },
    { value: 'clear', label: 'Clear' },
  ],
  compare_at: [
    { value: 'fixed', label: 'Fixed price' },
    { value: 'match', label: 'Match online' },
    { value: 'pct', label: '% of online' },
    { value: 'delta', label: '+/− online' },
    { value: 'clear', label: 'Clear' },
  ],
  online: [
    { value: 'fixed', label: 'Fixed price' },
    { value: 'pct', label: 'Adjust by %' },
    { value: 'delta', label: 'Adjust by $' },
  ],
};

const round2 = (n: number) => Math.max(0, Math.round(n * 100) / 100);

/**
 * Bulk-edit prices across the selected variants — the nursery price, the online
 * price, or the compare-at (sale) price. Nursery prices route through the
 * dedicated metafield push; online and compare-at go through the standard
 * variant push. Modes that derive from the online price skip variants that have
 * none.
 */
export function BulkPricingDialog({ open, onOpenChange, targets, onApplied }: Props) {
  const [target, setTarget] = useState<PriceTarget>('nursery');
  const [mode, setMode] = useState<Mode>('fixed');
  const [value, setValue] = useState('');
  const bulkNursery = useBulkSetNurseryPrices();
  const bulkVariant = useBulkUpdateVariantFields();
  const pending = bulkNursery.isPending || bulkVariant.isPending;

  const reset = () => {
    setValue('');
    setMode('fixed');
  };

  const handleTargetChange = (next: PriceTarget) => {
    setTarget(next);
    // online has no clear/match — fall back to fixed if the current mode is gone.
    if (!MODE_CHIPS[next].some((m) => m.value === mode)) setMode('fixed');
  };

  const handleApply = async () => {
    const variants = (await targets.resolveVariants().catch((err) => {
      toast.error(`Could not load variants: ${(err as Error).message}`);
      return null;
    }));
    if (!variants) return;
    if (variants.length === 0) {
      toast.error('No variants in the selection');
      return;
    }

    const num = parseFloat(value);
    const needsNumber = mode === 'fixed' || mode === 'pct' || mode === 'delta';
    if (needsNumber && isNaN(num)) {
      toast.error('Enter a number');
      return;
    }
    if (mode === 'fixed' && num < 0) {
      toast.error('Price cannot be negative');
      return;
    }

    /** Compute the new price for one variant, or undefined to skip it. */
    const priceFor = (online: number | null): number | null | undefined => {
      if (mode === 'clear') return null;
      if (mode === 'fixed') return round2(num);
      if (online == null) return undefined; // match/pct/delta need an online price
      if (mode === 'match') return round2(online);
      if (mode === 'pct') return round2(online * (1 + num / 100));
      return round2(online + num); // delta
    };

    try {
      if (target === 'nursery') {
        const updates = variants
          .map((v) => {
            const price = priceFor(v.online_price);
            return price === undefined
              ? null
              : { shopifyVariantId: v.shopify_variant_id, price };
          })
          .filter((u): u is { shopifyVariantId: number; price: number | null } => u !== null);
        if (updates.length === 0) {
          toast.error('None of the selected variants have an online price to base this on');
          return;
        }
        await bulkNursery.mutateAsync(updates);
        finish(updates.length, variants.length);
      } else {
        const column = target === 'online' ? 'price' : 'compare_at_price';
        const updates: VariantFieldUpdate[] = variants
          .map((v): VariantFieldUpdate | null => {
            const price = priceFor(v.online_price);
            return price === undefined
              ? null
              : {
                  shopifyVariantId: v.shopify_variant_id,
                  shopifyProductId: v.shopify_product_id,
                  fields: { [column]: price },
                };
          })
          .filter((u): u is VariantFieldUpdate => u !== null);
        if (updates.length === 0) {
          toast.error('None of the selected variants have an online price to base this on');
          return;
        }
        await bulkVariant.mutateAsync(updates);
        finish(updates.length, variants.length);
      }
    } catch (err) {
      toast.error(`Bulk price update failed: ${(err as Error).message}`);
    }
  };

  const finish = (applied: number, total: number) => {
    const skipped = total - applied;
    toast.success(
      `Updated price on ${applied} variants in Shopify` +
        (skipped > 0 ? ` (${skipped} skipped — no online price)` : ''),
    );
    reset();
    onOpenChange(false);
    onApplied();
  };

  const showInput = mode === 'fixed' || mode === 'pct' || mode === 'delta';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit pricing</DialogTitle>
          <DialogDescription>Applies to {targets.selectionLabel}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-medium text-muted-foreground">
              Which price
            </label>
            <FilterChips
              options={TARGET_CHIPS}
              value={target}
              onChange={(v) => handleTargetChange(v as PriceTarget)}
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-muted-foreground">
              How to set it
            </label>
            <FilterChips
              options={MODE_CHIPS[target]}
              value={mode}
              onChange={(v) => setMode(v as Mode)}
            />
          </div>

          {mode === 'match' && (
            <p className="text-xs text-muted-foreground">
              Each variant's {target === 'nursery' ? 'nursery' : 'compare-at'} price will be
              set equal to its current online price.
            </p>
          )}
          {mode === 'clear' && (
            <p className="text-xs text-muted-foreground">
              The {target === 'nursery' ? 'nursery' : 'compare-at'} price will be removed from
              every selected variant.
            </p>
          )}
          {showInput && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {mode === 'fixed'
                  ? 'Price ($)'
                  : mode === 'pct'
                    ? 'Percent'
                    : 'Dollar adjustment'}
              </label>
              <Input
                type="number"
                step={mode === 'pct' ? '1' : '0.01'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={
                  mode === 'fixed'
                    ? '0.00'
                    : mode === 'pct'
                      ? 'e.g. -10 for 10% below, 15 for 15% above'
                      : 'e.g. -2.00 for $2 below'
                }
                autoFocus
              />
              {(mode === 'pct' || mode === 'delta') && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Based on each variant's online price. Variants without one are skipped.
                  Result is never negative.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button className="w-full sm:w-auto" onClick={handleApply} disabled={pending}>
            {pending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
